import { captureMessage } from '../config/sentry.config';
import { AUTH_RETRY } from '../config/appConfig';

/**
 * Session Manager
 * Handles session cleanup and logout operations
 */

/**
 * Clears all authentication data from localStorage and redirects to login
 * This is used when tokens are invalid or expired and cannot be refreshed
 */
export const clearSessionAndRedirect = (reason: string = 'Session expired') => {
  console.warn('[SessionManager] Clearing session:', reason);

  // Report to Sentry
  captureMessage(`Session cleared: ${reason}`, 'warning');

  // Clear all localStorage data
  // This includes Auth0 tokens and any other cached data
  localStorage.clear();

  // Redirect to login page
  // Using window.location.href ensures a full page reload
  // This clears any in-memory state from Auth0 SDK
  window.location.href = '/login';
};

/**
 * Checks if an error is an authentication error that requires session cleanup
 * @param error The error to check
 * @returns true if the error requires session cleanup
 */
export const isAuthenticationError = (error: unknown): boolean => {
  if (!error) return false;

  // Check if it's an Auth0 error that requires re-authentication
  if (typeof error === 'object' && 'error' in error) {
    const errorCode = (error as { error: string }).error;
    const authErrors = [
      'login_required',
      'consent_required',
      'interaction_required',
      'invalid_grant',
      'invalid_token',
    ];
    return authErrors.includes(errorCode);
  }

  // Check if it's an HTTP 401 or 403 error
  if (typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    return status === 401 || status === 403;
  }

  // Check error message for common authentication error patterns
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('unauthorized') ||
      message.includes('authentication') ||
      message.includes('token') ||
      message.includes('login required')
    );
  }

  return false;
};

/**
 * Retry counter to prevent infinite loops
 * Tracks how many times we've attempted to get a token in a session
 */
class RetryCounter {
  private counters: Map<string, { count: number; lastReset: number }> = new Map();
  private readonly MAX_RETRIES = AUTH_RETRY.maxRetries;
  private readonly RESET_INTERVAL = AUTH_RETRY.resetIntervalMs;

  increment(key: string): number {
    const now = Date.now();
    const existing = this.counters.get(key);

    // Reset counter if it's been more than RESET_INTERVAL
    if (!existing || now - existing.lastReset > this.RESET_INTERVAL) {
      this.counters.set(key, { count: 1, lastReset: now });
      return 1;
    }

    // Increment counter
    const newCount = existing.count + 1;
    this.counters.set(key, { count: newCount, lastReset: existing.lastReset });
    return newCount;
  }

  hasExceededLimit(key: string): boolean {
    const counter = this.counters.get(key);
    return counter ? counter.count > this.MAX_RETRIES : false;
  }

  reset(key: string): void {
    this.counters.delete(key);
  }

  getCount(key: string): number {
    return this.counters.get(key)?.count || 0;
  }
}

// Export singleton instance
export const retryCounter = new RetryCounter();
